import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Edit, Calendar, Phone, Mail, MapPin, Info, ChevronDown, X } from 'lucide-react';
import { on } from 'events';
import CalendarModal from './CalendarModal';
import { buildApiUrl } from '../../../../services/apiConfig';

const API_HOST = process.env.REACT_APP_API_URL || '50.17.67.70';
const CUST_SUMMARY_ENDPOINT = buildApiUrl('CUSTOMER_SUMMARY', '/customer-summary');

// Widget Customization Modal Component
const WidgetCustomizationModal = ({
    isOpen,
    onClose,
    currentSettings,
    onSave
}) => {
    const [settings, setSettings] = useState(currentSettings || {});

    useEffect(() => {
        if (isOpen) {
            setSettings(currentSettings || {});
        }
    }, [isOpen, currentSettings]);

    const handleChange = (field, value) => {
        setSettings(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(settings);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 flex items-center justify-center z-50 backdrop-blur-sm">
            <div className="bg-white rounded-lg w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-primary">Widget Settings</h2>
                    <button
                        onClick={onClose}
                        className="p-1 rounded-full hover:bg-gray-100"
                    >
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    {/* AI Prompt Customization */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            AI Summary Prompt
                        </label>
                        <textarea
                            value={settings.summaryPrompt || ""}
                            onChange={(e) => handleChange('summaryPrompt', e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-md min-h-[100px]"
                            placeholder="Generate a concise summary of the client's portfolio and key insights..."
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Customize the prompt sent to the AI for generating client summaries.
                        </p>
                    </div>

                    {/* Display Options */}
                    <div className="mb-4">
                        <h3 className="font-medium text-gray-700 mb-2">Display Options</h3>

                        <div className="space-y-2">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={settings.showShortSummary !== false}
                                    onChange={(e) => handleChange('showShortSummary', e.target.checked)}
                                    className="mr-2"
                                />
                                <span className="text-sm">Show Short Summary</span>
                            </label>

                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={settings.showLongSummary !== false}
                                    onChange={(e) => handleChange('showLongSummary', e.target.checked)}
                                    className="mr-2"
                                />
                                <span className="text-sm">Show Long Summary</span>
                            </label>

                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={settings.showClientSelector !== false}
                                    onChange={(e) => handleChange('showClientSelector', e.target.checked)}
                                    className="mr-2"
                                />
                                <span className="text-sm">Show Client Selector</span>
                            </label>

                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={settings.showScheduleButton !== false}
                                    onChange={(e) => handleChange('showScheduleButton', e.target.checked)}
                                    className="mr-2"
                                />
                                <span className="text-sm">Show Schedule Call Button</span>
                            </label>
                        </div>
                    </div>

                    {/* Metrics Customization */}
                    <div className="mb-4">
                        <h3 className="font-medium text-gray-700 mb-2">Metrics Display</h3>
                        <p className="text-xs text-gray-500 mb-2">
                            Select which metrics to display in the bottom section
                        </p>

                        <div className="grid grid-cols-2 gap-2">
                            {[
                                { id: 'aum', label: 'Total AUM', tooltip: settings.tooltips?.aum || "Assets Under Management (AUM) represents the total market value of investments that the client has entrusted to your firm to manage." },
                                { id: 'ytdReturn', label: 'YTD Return', tooltip: settings.tooltips?.ytdReturn || "Year-to-Date (YTD) Return shows the percentage gain or loss on the client's investments since the beginning of the current calendar year." },
                                { id: 'timeHorizon', label: 'Time Horizon', tooltip: settings.tooltips?.timeHorizon || "Time Horizon indicates the client's investment timeframe, which influences their investment strategy and risk tolerance." },
                                { id: 'lastContact', label: 'Last Contact', tooltip: settings.tooltips?.lastContact || "Last Contact Date shows when you last communicated with this client, helping you maintain regular engagement." },
                                { id: 'riskTolerance', label: 'Risk Tolerance', tooltip: "The client's willingness to accept market volatility in exchange for potentially higher returns." },
                                { id: 'investmentGoal', label: 'Investment Goal', tooltip: "The primary financial objective the client is working towards." },
                                { id: 'nextReview', label: 'Next Review', tooltip: "The scheduled date for the next portfolio review with this client." },
                                { id: 'clientSince', label: 'Client Since', tooltip: "The date when this individual became your client." }
                            ].map(metric => (
                                <div key={metric.id} className="border rounded-md p-2">
                                    <label className="flex items-center justify-between">
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                checked={settings.metrics?.[metric.id] !== false}
                                                onChange={(e) => handleChange('metrics', {
                                                    ...(settings.metrics || {}),
                                                    [metric.id]: e.target.checked
                                                })}
                                                className="mr-2"
                                            />
                                            <span className="text-sm">{metric.label}</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const newTooltip = prompt("Edit tooltip text:", metric.tooltip);
                                                if (newTooltip !== null) {
                                                    handleChange('tooltips', {
                                                        ...(settings.tooltips || {}),
                                                        [metric.id]: newTooltip
                                                    });
                                                }
                                            }}
                                            className="text-xs text-primary hover:underline"
                                        >
                                            Edit Tooltip
                                        </button>
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 mt-6">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-secondary text-white rounded-md text-sm font-medium hover:bg-primaryLight"
                        >
                            Save Changes
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const ClientProfileHeader = ({
    customerData,
    widgetContents,
    longSummary,
    shortSummary,
    isLoading,
    onRefresh,
    allClients,
    onClientChange,
    widgetSettings,
    onEditWidget
}) => {


    const [key, setKey] = useState(0);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [showCalendarModal, setShowCalendarModal] = useState(false);
    const [isHovering, setIsHovering] = useState(false);

    useEffect(() => {
        setKey(prevKey => prevKey + 1);
        console.log("ClientProfileHeader re-rendered with key:", key);
    }, [customerData, longSummary, shortSummary, widgetSettings, allClients]);

    const formatAUM = (aum) => {
        if (!aum) return '$0';

        if (aum >= 1000000000) {
            return `$${(aum / 1000000000).toFixed(1)}B`;
        } else if (aum >= 1000000) {
            return `$${(aum / 1000000).toFixed(1)}M`;
        } else if (aum >= 1000) {
            return `$${(aum / 1000).toFixed(1)}K`;
        }
        return `$${aum.toFixed(0)}`;
    };

    const formatReturn = (returnValue) => {
        if (!returnValue) return '0%';
        const value = parseFloat(returnValue);
        return value >= 0 ? `+${value}%` : `${value}%`;
    };

    const handleClientSelect = (client) => {
        onClientChange(client);
        setDropdownOpen(false);
    };

    const getInitials = (name) => {
        if (!name) return '';
        return name
            .split(' ')
            .map(part => part[0])
            .join('')
            .toUpperCase()
            .substring(0, 2);
    };

    // Get active metrics based on settings
    const getActiveMetrics = () => {
        const defaultMetrics = ['aum', 'ytdReturn', 'timeHorizon', 'lastContact'];
        const metrics = widgetSettings?.metrics || {};

        return Object.entries(metrics)
            .filter(([_, isActive]) => isActive !== false)
            .map(([metricId]) => metricId)
            .filter(Boolean);
    };

    // Get metric data for display
    const getMetricData = (metricId) => {
        const tooltips = widgetSettings?.tooltips || {};

        switch (metricId) {
            case 'aum':
                return {
                    value: formatAUM(customerData?.total_aum || customerData?.net_worth),
                    label: 'Total AUM',
                    tooltip: tooltips.aum || "Assets Under Management (AUM) represents the total market value of investments that the client has entrusted to your firm to manage."
                };
            case 'ytdReturn':
                return {
                    value: formatReturn(customerData?.ytd_return || "5.2"),
                    label: 'YTD Return',
                    tooltip: tooltips.ytdReturn || "Year-to-Date (YTD) Return shows the percentage gain or loss on the client's investments since the beginning of the current calendar year.",
                    valueClass: parseFloat(customerData?.ytd_return) >= 0 ? 'text-success-dark' : 'text-error'
                };
            case 'timeHorizon':
                return {
                    value: customerData?.time_horizon_category ? `${customerData.time_horizon_category}` : "Medium-term",
                    label: 'Time Horizon',
                    tooltip: tooltips.timeHorizon || "Time Horizon indicates the client's investment timeframe, which influences their investment strategy and risk tolerance."
                };
            case 'lastContact':
                return {
                    value: customerData?.last_contact_date || "2023-05-15",
                    label: 'Last Contact',
                    tooltip: tooltips.lastContact || "Last Contact Date shows when you last communicated with this client, helping you maintain regular engagement."
                };
            case 'riskTolerance':
                return {
                    value: customerData?.risk_tolerance || "Moderate",
                    label: 'Risk Tolerance',
                    tooltip: tooltips.riskTolerance || "The client's willingness to accept market volatility in exchange for potentially higher returns."
                };
            case 'investmentGoal':
                return {
                    value: customerData?.investment_goal || "Retirement",
                    label: 'Investment Goal',
                    tooltip: tooltips.investmentGoal || "The primary financial objective the client is working towards."
                };
            case 'nextReview':
                return {
                    value: customerData?.next_review_date || "2023-12-15",
                    label: 'Next Review',
                    tooltip: tooltips.nextReview || "The scheduled date for the next portfolio review with this client."
                };
            case 'clientSince':
                return {
                    value: customerData?.client_since || "2018-03-22",
                    label: 'Client Since',
                    tooltip: tooltips.clientSince || "The date when this individual became your client."
                };
            default:
                return {
                    value: "N/A",
                    label: metricId,
                    tooltip: "No description available"
                };
        }
    };

    const activeMetrics = getActiveMetrics();

    // Apply custom styles from settings
    const headerStyle = {
        backgroundColor: widgetSettings?.headerColor || '#ffffff',
        color: widgetSettings?.textColor || '#000000'
    };



    return (
        <div
            key={key}
            className="rounded-xl p-6 shadow-sm border border-gray-100 mx-auto animate-[fadeInUp_0.3s_ease-out] hover:shadow-md transition-shadow duration-200 relative"
            style={headerStyle}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
        >
            {isHovering && (
                <button
                    onClick={onEditWidget}
                    className="absolute top-3 right-3 p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors duration-200 z-10"
                    title="Edit Widget Settings"
                >
                    <Edit size={16} className="text-gray-600" />
                </button>
            )}

            <div className="flex items-start justify-between mb-6">
                <div className="flex items-start gap-5">
                    {customerData?.img ? (
                        <img
                            src={customerData.img}
                            className="w-20 h-20 rounded-full object-cover flex-shrink-0 hover:scale-104 transition-transform duration-200"
                            alt={customerData?.name || "Client"}
                        />
                    ) : (
                        <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center text-xl font-semibold text-primaryLight flex-shrink-0 hover:scale-104 transition-transform duration-200">
                            {getInitials(customerData?.name)}
                        </div>
                    )}
                    <div className="flex flex-col">
                        <div className="relative inline-block">
                            <h1
                                className={`text-2xl font-bold text-primary mb-1 flex items-center gap-1.5 ${widgetSettings?.showClientSelector !== false && allClients && allClients.length > 0 ? 'cursor-pointer select-none' : ''}`}
                                onClick={() => widgetSettings?.showClientSelector !== false && allClients && allClients.length > 0 && setDropdownOpen(!dropdownOpen)}
                                style={{ color: widgetSettings?.textColor || 'inherit' }}
                            >
                                {customerData?.name || "No clients available"}
                                {widgetSettings?.showClientSelector !== false && allClients && allClients.length > 0 && (
                                    <ChevronDown size={20} className={`transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
                                )}
                            </h1>

                            {dropdownOpen && widgetSettings?.showClientSelector !== false && allClients && allClients.length > 0 && (
                                <div className="absolute top-full left-0 z-[1000] min-w-[180px] max-h-[250px] overflow-y-auto bg-white rounded-md shadow-lg border border-gray-200 py-1.5 mt-0.5 animate-[fadeIn_0.2s_ease]">
                                    {allClients.map(client => (
                                        <div
                                            key={client.id}
                                            className="py-1.5 px-3 cursor-pointer transition-colors duration-200 text-sm whitespace-nowrap overflow-hidden text-ellipsis hover:bg-gray-100"
                                            onClick={() => handleClientSelect(client)}
                                        >
                                            {client.name}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="relative my-1 pr-6">
                            {isLoading ? (
                                <div className="text-sm text-gray-600 my-1 italic flex items-center after:content-[''] after:animate-ellipsis after:w-6 after:text-left after:inline-block">
                                    Generating client insights...
                                </div>
                            ) : (
                                <>
                                    {shortSummary && widgetSettings?.showShortSummary !== false && (
                                        <p className="text-base font-medium text-secondary m-0 leading-relaxed italic"
                                            style={{ color: widgetSettings?.textColor ? `${widgetSettings.textColor}99` : 'inherit' }}>
                                            {shortSummary}
                                        </p>
                                    )}
                                    {longSummary && widgetSettings?.showLongSummary !== false && (
                                        <div className="text-sm text-gray-600 mt-1 mb-1.5 leading-relaxed max-w-[95%]"
                                            style={{ color: widgetSettings?.textColor ? `${widgetSettings.textColor}99` : 'inherit' }}>
                                            <p>{longSummary}</p>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        <p className="text-base text-gray-500 mb-5" style={{ color: widgetSettings?.textColor ? `${widgetSettings.textColor}99` : 'inherit' }}>
                            {customerData?.net_worth_category === 'High' ? 'High Net Worth Individual' :
                                customerData?.net_worth_category === 'Medium' ? 'Medium Net Worth Individual' :
                                    'Standard Client'}
                        </p>

                        <div className="flex gap-8">
                            <div className="flex items-center gap-2.5 text-gray-500" style={{ color: widgetSettings?.textColor ? `${widgetSettings.textColor}99` : 'inherit' }}>
                                <Phone className="w-3 h-3 flex-shrink-0" />
                                <span>{customerData?.phone || "N/A"}</span>
                            </div>
                            <div className="flex items-center gap-2.5 text-gray-500" style={{ color: widgetSettings?.textColor ? `${widgetSettings.textColor}99` : 'inherit' }}>
                                <Mail className="w-3 h-3 flex-shrink-0" />
                                <span>{customerData?.email || "N/A"}</span>
                            </div>
                            <div className="flex items-center gap-2.5 text-gray-500" style={{ color: widgetSettings?.textColor ? `${widgetSettings.textColor}99` : 'inherit' }}>
                                <MapPin className="w-3 h-3 flex-shrink-0" />
                                <span>{customerData?.address || "N/A"}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* {widgetSettings?.showScheduleButton !== false && (
                    <div className="flex gap-2.5 flex-shrink-0">
                        <button
                            className="flex items-center gap-1.5 py-2.5 px-5 rounded-lg font-medium bg-secondary text-white border-none cursor-pointer transition-all duration-200 whitespace-nowrap text-sm hover:bg-primaryLight"
                            onClick={() => setShowCalendarModal(true)}
                        >
                            <Calendar className="w-3 h-3" />
                            <span>Schedule Call</span>
                        </button>
                    </div>
                )} */}
                <CalendarModal
                    isOpen={showCalendarModal}
                    onClose={() => setShowCalendarModal(false)}
                    customerId={customerData?.id}
                    customerName={customerData?.name}
                />
            </div>

            <div className={`grid gap-6 pt-5 border-t border-gray-100 ${activeMetrics.length > 4 ? 'grid-cols-5 lg:grid-cols-5 md:grid-cols-3 sm:grid-cols-2' : `grid-cols-${activeMetrics.length} lg:grid-cols-${activeMetrics.length} md:grid-cols-2 sm:grid-cols-2`} xs:grid-cols-1`}>
                {activeMetrics.map(metricId => {
                    const metric = getMetricData(metricId);
                    return (
                        <div key={metricId} className="text-center relative group">
                            <div className={`text-2xl font-bold mb-0.5 leading-tight cursor-help ${metric.valueClass || 'text-black'}`}
                                style={{ color: metric.valueClass ? 'inherit' : (widgetSettings?.textColor || 'inherit') }}>
                                {metric.value}
                                <div className="invisible group-hover:visible opacity-0 group-hover:opacity-100 absolute bottom-[125%] left-1/2 -translate-x-1/2 bg-white text-gray-600 py-1.5 px-2.5 rounded text-sm w-[200px] z-[100] shadow-sm text-center pointer-events-none border border-gray-200 transition-opacity duration-200">
                                    {metric.tooltip}
                                    <div className="absolute top-full left-1/2 -ml-1 border-4 border-transparent border-t-gray-200"></div>
                                </div>
                            </div>
                            <div className="text-sm text-gray-500" style={{ color: widgetSettings?.textColor ? `${widgetSettings.textColor}99` : 'inherit' }}>
                                {metric.label}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const ClientProfile = ({ widgetContents = {}, clientData = null, allClients = [], onClientChange }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [summary, setSummary] = useState(null);
    const [fetchAttempts, setFetchAttempts] = useState(0);
    const [lastFetchedCustomerId, setLastFetchedCustomerId] = useState(null);
    const [lastContentHash, setLastContentHash] = useState(null);
    const [customerData, setCustomerData] = useState(null);
    const [isLoadingCustomerData, setIsLoadingCustomerData] = useState(false);
    const [noClientsAvailable, setNoClientsAvailable] = useState(false);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const [widgetsLoaded, setWidgetsLoaded] = useState(false);

    // Track in-flight requests
    const loadingRef = useRef(false);
    // Track if component is mounted
    const isMountedRef = useRef(true);

    // New state for widget customization
    const [showCustomizationModal, setShowCustomizationModal] = useState(false);
    const [widgetSettings, setWidgetSettings] = useState(() => {
        // Try to load saved settings from localStorage
        const savedSettings = localStorage.getItem('clientProfileWidgetSettings');
        return savedSettings ? JSON.parse(savedSettings) : {
            summaryPrompt: "Generate a concise summary of the client's portfolio and key insights. Include one short sentence summary and a more detailed 2-3 sentence analysis.",
            showShortSummary: true,
            showLongSummary: true,
            showClientSelector: true,
            showScheduleButton: true,
            metrics: {
                aum: true,
                ytdReturn: true,
                timeHorizon: true,
                lastContact: true,
                riskTolerance: false,
                investmentGoal: false,
                nextReview: false,
                clientSince: false
            },
            tooltips: {
                aum: "Assets Under Management (AUM) represents the total market value of investments that the client has entrusted to your firm to manage.",
                ytdReturn: "Year-to-Date (YTD) Return shows the percentage gain or loss on the client's investments since the beginning of the current calendar year.",
                timeHorizon: "Time Horizon indicates the client's investment timeframe, which influences their investment strategy and risk tolerance.",
                lastContact: "Last Contact Date shows when you last communicated with this client, helping you maintain regular engagement.",
                riskTolerance: "The client's willingness to accept market volatility in exchange for potentially higher returns.",
                investmentGoal: "The primary financial objective the client is working towards.",
                nextReview: "The scheduled date for the next portfolio review with this client.",
                clientSince: "The date when this individual became your client."
            },
            headerColor: "#ffffff",
            textColor: "#000000"
        };
    });

    const widgetContentRef = useRef(widgetContents);

    // Set up cleanup on unmount
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // Track widget content changes
    useEffect(() => {
        widgetContentRef.current = widgetContents;

        // If we have widget contents and a customer, consider triggering a summary update
        if (Object.keys(widgetContents).length > 0 && customerData && initialLoadComplete) {
            const timer = setTimeout(() => {
                fetchSummary();
            }, 1000);

            return () => clearTimeout(timer);
        }
    }, [widgetContents]);

    // Handle client data changes
    useEffect(() => {
        if (!allClients || allClients.length === 0) {
            setNoClientsAvailable(true);
            setCustomerData(null);
            return;
        } else {
            setNoClientsAvailable(false);
        }

        if (clientData) {
            setCustomerData(clientData);

            if (lastFetchedCustomerId !== clientData.id) {
                setSummary(null);
                setLastFetchedCustomerId(clientData.id);
                setInitialLoadComplete(false);
                setWidgetsLoaded(false);

                // Check if we have widget contents to trigger an immediate summary
                if (Object.keys(widgetContents).length > 0) {
                    // Small delay to ensure state updates have propagated
                    setTimeout(() => {
                        fetchSummary(true);
                    }, 300);
                }
            }
        }
    }, [clientData, allClients]);

    // Save settings to localStorage when they change
    useEffect(() => {
        localStorage.setItem('clientProfileWidgetSettings', JSON.stringify(widgetSettings));
    }, [widgetSettings]);

    const generateContentHash = useCallback(() => {
        const contentString = JSON.stringify(widgetContentRef.current);
        let hash = 0;
        for (let i = 0; i < contentString.length; i++) {
            const char = contentString.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash;
        }
        return hash.toString();
    }, []);

    const fetchSummary = useCallback(async (forceRefresh = false) => {
        // Prevent duplicate requests and check prerequisites
        if (loadingRef.current || !customerData?.id) return;

        const widgetContentCount = Object.keys(widgetContentRef.current || {}).length;

        // Don't fetch if we don't have any widget content
        if (widgetContentCount === 0) {
            return;
        }

        if (forceRefresh) {
            setFetchAttempts(0);
        } else if (fetchAttempts > 5) {
            return;
        }

        const currentContentHash = generateContentHash();

        // Skip if nothing has changed and we're not forcing a refresh
        if (!forceRefresh && customerData.id === lastFetchedCustomerId && currentContentHash === lastContentHash) {
            return;
        }

        loadingRef.current = true;
        setLoading(true);
        setFetchAttempts(prev => prev + 1);

        try {
            const widgetTexts = Object.entries(widgetContentRef.current || {}).map(([widgetId, content]) => {
                let formattedContent;

                if (typeof content === 'string') {
                    formattedContent = content.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
                } else if (typeof content === 'object') {
                    try {
                        formattedContent = JSON.stringify(content);
                    } catch (e) {
                        formattedContent = String(content);
                    }
                } else {
                    formattedContent = String(content || "");
                }

                return formattedContent;
            }).filter(content => content.length > 0);

            const formattedContent = widgetTexts.join('\n\n');

            if (!formattedContent.trim()) {
                if (isMountedRef.current) {
                    setLoading(false);
                    loadingRef.current = false;
                }
                return;
            }

            const response = await fetch(CUST_SUMMARY_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    content: formattedContent,
                    customerName: customerData.name,
                    userPrompt: widgetSettings.summaryPrompt
                })
            });

            if (!response.ok) {
                throw new Error(`API responded with status ${response.status}`);
            }

            const data = await response.json();

            if (isMountedRef.current) {
                // Make sure to set loading to false BEFORE updating the summary
                // This ensures the UI will show the summary instead of the loading indicator
                setLoading(false);
                loadingRef.current = false;

                // Now update the summary
                setSummary({
                    shortSummary: data.shortSummary,
                    longSummary: data.longSummary
                });

                setLastFetchedCustomerId(customerData.id);
                setLastContentHash(currentContentHash);
                setError(null);
                setInitialLoadComplete(true);
            }
        } catch (err) {
            console.error("Error generating summary:", err);
            if (isMountedRef.current) {
                setError("Failed to generate summary. Please try again.");
                setLoading(false);
                loadingRef.current = false;
            }
        }
    }, [customerData, generateContentHash, lastContentHash, lastFetchedCustomerId, fetchAttempts, widgetSettings.summaryPrompt]);

    // Initial load effect - wait for widgets to load
    useEffect(() => {
        if (!customerData || initialLoadComplete) return;

        // Check if we have widget contents
        const hasWidgetContents = Object.keys(widgetContents).length > 0;

        if (hasWidgetContents) {
            const timer = setTimeout(() => {
                fetchSummary(true);
            }, 500);

            return () => clearTimeout(timer);
        }
    }, [widgetContents, fetchSummary, customerData, initialLoadComplete]);

    const handleRefresh = () => {
        fetchSummary(true);
    };

    const handleSaveSettings = (newSettings) => {
        const promptChanged = newSettings.summaryPrompt !== widgetSettings.summaryPrompt;
        setWidgetSettings(newSettings);

        if (promptChanged && Object.keys(widgetContents).length > 0) {
            setTimeout(() => {
                fetchSummary(true);
            }, 300);
        }
    };

    const shortSummary = summary?.shortSummary || "";
    const longSummary = summary?.longSummary || "";

    return (
        <div className="bg-gray-50 p-4">
            <div className="max-w-full mx-auto">
                {isLoadingCustomerData ? (
                    <div className="text-center py-4 text-gray-600">Loading customer data...</div>
                ) : noClientsAvailable ? (
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mx-auto text-center">
                        <div className="py-8">
                            <div className="text-xl font-semibold text-gray-700 mb-2">No Clients Available</div>
                            <p className="text-gray-500">
                                You don't have access to any clients. Please contact your administrator for assistance.
                            </p>
                        </div>
                    </div>
                ) : (
                    <ClientProfileHeader
                        customerData={customerData}
                        widgetContents={widgetContents}
                        longSummary={longSummary}
                        shortSummary={shortSummary}
                        isLoading={loading}
                        onRefresh={handleRefresh}
                        allClients={allClients}
                        onClientChange={onClientChange}
                        widgetSettings={widgetSettings}
                        onEditWidget={() => setShowCustomizationModal(true)}
                    />
                )}
                {error && !noClientsAvailable && (
                    <div className="text-error text-xs my-1 p-1 bg-error-light rounded text-center">
                        {error}
                    </div>
                )}
            </div>

            {/* Widget Customization Modal */}
            <WidgetCustomizationModal
                isOpen={showCustomizationModal}
                onClose={() => setShowCustomizationModal(false)}
                currentSettings={widgetSettings}
                onSave={handleSaveSettings}
            />
        </div>
    );
};

export default ClientProfile;